import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { Role, User } from 'common/src/utils/types';
import { createUser, updateUser } from 'common/src/api/users';
import { toast } from 'react-toastify';
import useGeneralStore from '../../../store/useGeneralStore';

interface ModalCreateEditUserProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User;
}

interface UserFormData extends Partial<User> {
  password?: string;
}

const ModalCreateEditUser: React.FC<ModalCreateEditUserProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const { fetchUsers } = useGeneralStore();
  const [formData, setFormData] = useState<UserFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    role: [],
    password: '', // Add password field
  });

  useEffect(() => {
    if (user) {
      setFormData(user);
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        role: [],
        password: '', // Reset password field
      });
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (roles: string[]) => {
    setFormData((prev) => ({ ...prev, role: roles as Role[] }));
  };

  const handleSave = async () => {
    if (user) {
      // Edit existing user
      const res = await updateUser(formData as User);
      if (res) {
        toast.success('Usuario actualizado correctamente');
        fetchUsers();
      } else {
        toast.error('Error al actualizar el usuario');
      }
    } else {
      const { first_name, last_name, email, phone_number, password, role } =
        formData;

      if (
        !first_name ||
        !last_name ||
        !email ||
        !phone_number ||
        !password ||
        role?.length === 0
      ) {
        toast.error('Todos los campos son obligatorios');
        return;
      }
      const res = await createUser({
        first_name,
        last_name,
        email,
        phone_number,
        password,
        role: role as Role[],
      });
      if (res) {
        toast.success('Usuario creado correctamente');
        fetchUsers();
      } else {
        toast.error('Error al crear el usuario');
      }
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='2xl'>
      <ModalContent>
        <ModalHeader>{user ? 'Editar Usuario' : 'Crear Usuario'}</ModalHeader>
        <ModalBody className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <Input
            label='First Name'
            name='first_name'
            value={formData.first_name}
            onChange={handleInputChange}
          />
          <Input
            label='Last Name'
            name='last_name'
            value={formData.last_name}
            onChange={handleInputChange}
          />
          <Input
            label='Phone Number'
            name='phone_number'
            value={formData.phone_number}
            onChange={handleInputChange}
          />

          <Input
            label='Email'
            name='email'
            value={formData.email}
            onChange={handleInputChange}
            readOnly={user ? true : false}
          />

          {!user && ( // Only show password field when creating a new user
            <Input
              label='Password'
              name='password'
              type='password'
              value={formData.password}
              onChange={handleInputChange}
            />
          )}

          <div>
            <Select
              label='Roles'
              placeholder='Select roles'
              selectionMode='multiple'
              selectedKeys={formData.role}
              onSelectionChange={(keys) =>
                handleRoleChange(Array.from(keys) as string[])
              }
              className='col-span-2'
            >
              <SelectItem key='admin' value='admin'>
                Admin
              </SelectItem>
              <SelectItem key='helper' value='helper'>
                Helper
              </SelectItem>
            </Select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color='danger'
            className='text-purple-500 border border-purple-500'
            variant='light'
            onPress={onClose}
          >
            Cancelar
          </Button>
          <Button
            color='primary'
            onPress={handleSave}
            className='bg-purple-500 text-white'
          >
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalCreateEditUser;
